// src/stores/useAdminRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { RequestReporterType, ProxyDetails, PatientDetails } from '../types';
import { submitAdminRequest } from '../services/admin-request.service';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';
import { useApiLoading } from '../lib/composables/useApiLoading';
import { useAttachments } from '../lib/composables/useAttachments';
import { useSubmitAction } from '../lib/composables/useSubmitAction';
import { usePatientIdentity } from '../lib/composables/usePatientIdentity';

export const useAdminRequestStore = defineStore('admin-request', () => {
  const expiryTime = ref<Date | null>(null); // +1 hour

  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const isEmergency = ref<boolean>(false);
  const reporterType = ref<RequestReporterType | null>(null);
  const {
    patientDetails,
    isValidPatient,
    isVerified,
    isNhsVerified,
    setPatientDetails,
    setIsValidPatient,
    setIsNhsVerified,
    resetIdentity,
  } = usePatientIdentity();
  // unique after here, above can be made modular and shared

  const proxyDetails = ref<ProxyDetails | null>();

  const adminRequest = ref<string>('');
  const { attachments, addAttachment, addAttachments, removeAttachment, clearAttachments } =
    useAttachments({ max: 10 });

  const isExpired = computed(
    () => expiryTime.value !== null && Date.now() > expiryTime.value.getTime(),
  );
  const isProxy = computed(() => reporterType.value === 'PROXY');

  function startNew() {
    adminRequest.value = '';
    clearAttachments();
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000); // +1 hour
    isEmergency.value = false;
    resetIdentity();
    proxyDetails.value = null;
    reporterType.value = null;
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setReporterType(value: RequestReporterType) {
    reporterType.value = value;
  }

  function setProxyDetails(details: ProxyDetails | null) {
    proxyDetails.value = details;
  }

  const verifyPatientDetails = useVerifyPatientDetails({
    patientDetails,
    isProxy: isProxy,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
  });

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setReporterType('PATIENT');
    }
    return result;
  };

  const submitAction = () =>
    useSubmitAction({ setApiLoading, setApiDone, endCurrent })(() =>
      submitAdminRequest({
        reporterType: reporterType.value,
        proxyDetails: proxyDetails.value,
        patientDetails: patientDetails.value,
        adminRequest: adminRequest.value,
        attachments: attachments.value,
      }),
    );

  function setAdminRequest(text: string) {
    adminRequest.value = text;
  }

  function setNhsSsoDetails(patientDetails: PatientDetails) {
    setIsNhsVerified(true);
    setPatientDetails(patientDetails);
    setIsValidPatient(true);
    setReporterType('PATIENT');
  }

  // ---- Expose flat state ----
  return {
    // apiLoading,
    apiLoading,
    // state
    expiryTime,

    isEmergency,
    reporterType,

    proxyDetails,
    patientDetails,
    isValidPatient,
    isVerified,
    isNhsVerified,
    isProxy,
    adminRequest,
    attachments,

    isExpired,

    // actions
    addAttachment,
    addAttachments,
    endCurrent,
    removeAttachment,
    setAdminRequest,
    setIsEmergency,
    setNhsSsoDetails: setNhsSsoDetails,
    setIsNhsVerified,
    setPatientDetails,
    setProxyDetails,
    setReporterType,
    startNew,
    submitAction,
    //
    verifyPatientDetails,
    verifyNhsLogin,
  };
});
